import React, { useState, useEffect } from "react";
import { Box, Typography, useTheme } from "@mui/material";


export default function PythonCodeEditorOutput({
  code,
  pyodide,
  loading = false,
  themeName: externalTheme
}) {
  const [output, setOutput] = useState([]);
  const muiTheme = useTheme();
  const isDarkMode = muiTheme.palette.mode === "dark";

  const outputBg = isDarkMode ? "#0d1117" : "#f6f8fa";
  const outputText = isDarkMode ? "#3fb950" : "#0969da";
  const errorText = isDarkMode ? "#ff7b72" : "#cf222e";

  useEffect(() => {
    if (!code || !code.trim() || !pyodide) {
      setOutput([]);
      return;
    }

    const runPython = async () => {
      try {
        const rawLogs = [];
        pyodide.setStdout({ batched: (msg) => rawLogs.push({ text: msg, isError: false }) });
        pyodide.setStderr({ batched: (msg) => rawLogs.push({ text: `❌ ${msg}`, isError: true }) });
        const result = await pyodide.runPythonAsync(code);
        if (result !== undefined && result !== null) {
          rawLogs.push({ text: String(result), isError: false });
        }

        // Split multi-line logs into individual line items
        const lineList = [];
        rawLogs.forEach((item) => {
          const lines = String(item.text).split(/\r?\n/);
          lines.forEach((l) => {
            lineList.push({ text: l, isError: item.isError });
          });
        });

        setOutput(lineList);
      } catch (err) {
        const errLines = String(err.message).split(/\r?\n/);
        setOutput(errLines.map((l, i) => ({
          text: i === 0 ? `❌ ${l}` : `   ${l}`,
          isError: true,
        })));
      }
    };

    runPython();
  }, [code, pyodide]);

  // Format output with line numbers and error highlighting
  const formatOutput = (outputLines) => {
    if (!outputLines || outputLines.length === 0) return null;

    return outputLines.map((item, index) => {
      const lineNumber = index + 1;
      const isErr = item.isError || item.text.startsWith("❌");
      return (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "flex-start",
            fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
            fontSize: "0.9rem",
            lineHeight: 1.6,
            mb: 0.3,
            backgroundColor: isErr ? (isDarkMode ? "rgba(255, 107, 107, 0.08)" : "rgba(207, 34, 46, 0.06)") : "transparent",
            borderRadius: "4px",
            px: 0.5,
          }}
        >
          <Box
            sx={{
              color: isDarkMode ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.3)",
              minWidth: "32px",
              textAlign: "right",
              paddingRight: 1.5,
              userSelect: "none",
              fontSize: "0.82rem",
            }}
          >
            {lineNumber}
          </Box>
          <Box
            sx={{
              flex: 1,
              color: isErr ? errorText : outputText,
              whiteSpace: "pre-wrap",
              wordWrap: "break-word",
              fontWeight: isErr ? 600 : 400,
            }}
          >
            {item.text}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        borderRadius: "8px",
        p: 2,
        background: outputBg,
        fontFamily: "'Fira Code', 'Cascadia Code', 'Consolas', monospace",
        fontSize: "0.9rem",
        overflowY: "auto",
        border: `1px solid ${
          isDarkMode ? "rgba(255,255,255,0.08)" : muiTheme.palette.divider
        }`,
        transition: "background 0.3s ease, border-color 0.3s ease",
        scrollbarWidth: "thin",
        scrollbarColor: isDarkMode
          ? "rgba(255, 255, 255, 0.2) transparent"
          : "rgba(0, 0, 0, 0.2) transparent",
        "&::-webkit-scrollbar": { width: "6px", height: "6px" },
        "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: isDarkMode ? "rgba(255, 255, 255, 0.2)" : "rgba(0, 0, 0, 0.2)",
          borderRadius: "10px",
        },
      }}
    >
      {loading ? (
        <Typography sx={{ color: isDarkMode ? "#58a6ff" : "#0969da", fontFamily: "inherit" }}>
          ⏳ Executing Python in WebAssembly...
        </Typography>
      ) : output.length > 0 ? (
        formatOutput(output)
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            color: isDarkMode ? "rgba(255,255,255,0.4)" : "rgba(0,0,0,0.4)",
            textAlign: "center",
            gap: 1,
          }}
        >
          <Typography variant="body2" sx={{ fontFamily: "inherit" }}>
            💡 Output will appear here after execution
          </Typography>
          <Typography variant="caption" sx={{ color: isDarkMode ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.3)" }}>
            Tip: Press <strong>Ctrl + Enter</strong> to run anytime
          </Typography>
        </Box>
      )}
    </Box>
  );
}
